<template>
    <PageErrorFiveHundred :errors="error">
        <ThButton @click="reload">Try Again</ThButton>
    </PageErrorFiveHundred>
</template>

<script setup lang="ts">
import Cookie from 'js-cookie'
import {
    getDocument,
    getLocalStorage,
    getNavigator,
    getSessionStorage,
    getWindow,
} from '@/helpers/globals'
import { parseCookies } from '@/helpers/cookie/cookie'
import type { ErrorCollectionWithTraces } from '@/helpers/errors'

import ThButton from '@/components/ui/buttons/ThButton.vue'
import PageErrorFiveHundred from '@/components/PageErrorFiveHundred.vue'

defineProps<{
    error: ErrorCollectionWithTraces
}>()

const reload = async () => {
    const navigator = getNavigator()

    if (navigator && typeof navigator.serviceWorker !== 'undefined') {
        // Unregister service workers
        await navigator.serviceWorker
            .getRegistrations()
            .then(function (registrations) {
                for (const registration of registrations) {
                    registration.unregister()
                }
            })
    }

    const localStorage = getLocalStorage()

    if (localStorage) {
        // Clear Local Storage
        localStorage.clear()
    }

    const sessionStorage = getSessionStorage()

    if (sessionStorage) {
        // Clear Session Storage
        sessionStorage.clear()
    }

    const document = getDocument()

    if (document) {
        // Clear Cookies
        Object.keys(parseCookies(document.cookie)).forEach((c) => {
            Cookie.remove(c)
        })
    }

    const window = getWindow()

    if (
        window &&
        'databases' in window.indexedDB &&
        typeof window.indexedDB.databases === 'function'
    ) {
        // Clear Databases
        await window.indexedDB.databases().then((dbs) => {
            dbs.map((db) => {
                if (!db.name) {
                    return Promise.resolve()
                }

                const dbRequest = window.indexedDB.open(db.name, db.version)

                return new Promise((resolve, reject) => {
                    dbRequest.onsuccess = () => {
                        const db = dbRequest.result

                        const storeNames: string[] = []

                        for (let i = 0; i < db.objectStoreNames.length; i++) {
                            const storeName = db.objectStoreNames.item(i)

                            if (!storeName) {
                                continue
                            }

                            storeNames.push(storeName)
                        }

                        return Promise.all(
                            storeNames.map((name) => {
                                return new Promise((resolve, reject) => {
                                    const transaction = db.transaction(
                                        [name],
                                        'readwrite'
                                    )

                                    const objectStore =
                                        transaction.objectStore(name)

                                    const objectStoreRequest =
                                        objectStore.clear()

                                    objectStoreRequest.onsuccess = () => {
                                        resolve(undefined)
                                    }
                                    objectStoreRequest.onerror = (e) => {
                                        reject(e)
                                    }
                                })
                            })
                        ).then(resolve)
                    }

                    dbRequest.onerror = (e) => {
                        reject(e)
                    }
                })
            })
        })
    }

    // And finally reload
    window?.location.reload()
}
</script>

<style lang="scss" scoped>
.page-500 {
    padding: 25vh 0;
}
</style>
